import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CHAT_DATA_ACCESS_CONFIG,
  ChatDataAccessConfig,
  ChatDataActions,
  selectActiveChat,
  selectActiveChatFiles,
  selectDataOrigin,
  selectDeletingHistoryFile,
  selectGeneratingMessage,
  selectTempFiles,
} from '@iese-chatbot/chat-data-access';
import { Chat, ChatFile, ChatUtils, DataOrigin, FileUtils } from '@iese-chatbot/common-utils';
import { TextareaAutoresizeDirective } from '@iese-chatbot/ui-design-system';
import { Store } from '@ngrx/store';
import { BdcWalkModule, BdcWalkService } from 'bdc-walkthrough';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { DataOriginSelectorComponent } from '../data-origin-selector/data-origin-selector.component';
import { UploadFilesListComponent } from '../upload-files-list/upload-files-list.component';

@Component({
  selector: 'icbc-main-chat-box',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CheckboxModule,
    TooltipModule,
    TextareaAutoresizeDirective,
    UploadFilesListComponent,
    SelectButtonModule,
    DataOriginSelectorComponent,
    BdcWalkModule,
  ],
  templateUrl: './main-chat-box.component.html',
})
export class MainChatBoxComponent implements OnInit, OnDestroy {
  @ViewChild(TextareaAutoresizeDirective)
  protected textareaResize!: TextareaAutoresizeDirective;

  protected mock = false;
  protected fromIESEKnowledge = true;
  protected currentMessage = '';
  protected activeChat: Chat | undefined;
  protected generatingMessage = false;
  protected deletingFile = false;
  protected tempFiles: File[] = [];
  protected chatFiles: ChatFile[] = [];
  protected dataOrigin: DataOrigin = DataOrigin.REPOSITORY;

  private _unsubscribe$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject(CHAT_DATA_ACCESS_CONFIG) private config: ChatDataAccessConfig,
    private store: Store,
    private router: Router,
    protected bdcWalkService: BdcWalkService,
  ) {}

  ngOnDestroy(): void {
    this._unsubscribe$.next(true);
    this._unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.mock = this.config.mock;
    this.store
      .select(selectActiveChat)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe({
        next: (activeChat) => {
          this.activeChat = activeChat;
          this.fromIESEKnowledge = activeChat?.messages[activeChat?.messages.length - 1].fromIESEKnowledge ?? true;
        },
      });
    this.store
      .select(selectGeneratingMessage)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe({
        next: (generatingMessage) => {
          this.generatingMessage = generatingMessage;
        },
      });
    this.store
      .select(selectTempFiles)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((files) => {
        this.tempFiles = files;
      });
    this.store
      .select(selectActiveChatFiles)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((chatFiles) => {
        this.chatFiles = chatFiles;
      });
    this.store
      .select(selectDataOrigin)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((dataOrigin) => {
        this.dataOrigin = dataOrigin;
      });
    this.store
      .select(selectDeletingHistoryFile)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((deletingFile) => {
        this.deletingFile = deletingFile;
      });
  }

  // TODO código duplicado
  endOnboardingTour() {
    this.bdcWalkService.setTaskCompleted('dialogStep1', true);
    this.bdcWalkService.setTaskCompleted('dialogStep2', true);
    this.bdcWalkService.setTaskCompleted('popupStep3', true);
    this.bdcWalkService.setTaskCompleted('popupStep4', true);
    this.bdcWalkService.setTaskCompleted('popupStep5', true);
    // this.bdcWalkService.setTaskCompleted('popupStep6', true);
    // this.bdcWalkService.setTaskCompleted('popupStep7', true);
    this.bdcWalkService.setTaskCompleted('dialogStep8', true);
    this.bdcWalkService.setTaskCompleted('popupStep9', true);
    this.bdcWalkService.setTaskCompleted('popupStepOwnFiles', true);
    this.bdcWalkService.setTaskCompleted('popupStepSelectOrigin', true);
    this.bdcWalkService.setTaskCompleted('popupStepCombineOrigin', true);
    // this.bdcWalkService.setTaskCompleted('popupStep10', true);
    this.bdcWalkService.setTaskCompleted('popupStep11', true);
    this.bdcWalkService.setTaskCompleted('dialogStep12', true);
    this.bdcWalkService.setTaskCompleted('dialogStep13', true);
  }

  protected sendMessage() {
    const message = ChatUtils.normalizeMessage(this.currentMessage);
    if (!message) return;

    if (!this.activeChat) {
      const payload = ChatUtils.createAddChatPayload(message, this.dataOrigin, this.tempFiles);
      this.store.dispatch(ChatDataActions.addChat(payload));
      this.router.navigate(['', 'chat', payload.chat.id]).then();
      this.emptyPrompt();
    } else {
      const payload = ChatUtils.createAddMessagePayload(this.activeChat, message, this.dataOrigin, this.tempFiles);
      this.store.dispatch(ChatDataActions.addMessage(payload));
      this.emptyPrompt();
    }
  }

  protected stopMessage() {
    if (this.activeChat) {
      const payload = { chat: this.activeChat, manually: true };
      this.store.dispatch(ChatDataActions.completeMessageStream(payload));
    }
  }

  protected onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  protected uploadFile() {
    FileUtils.forceOpenFileDialog();
  }

  private emptyPrompt() {
    this.currentMessage = '';
    this.textareaResize.forceResize();
    this.store.dispatch(ChatDataActions.clearFiles());
  }
}
